import intersection from 'lodash/intersection';
import { loadExternalData, loadApiData } from './ApiUtil';
import _get from 'lodash/get';

/** Props shape accepted by hasAccess / AccessControlWrapper-style checks. */
export interface AccessControlProps {
  organizationType?: string;
  requiredOrganizationType?: string;
  businessLines?: string[];
  requiredBusinessLine?: string | string[];
  capabilities?: string[];
  requiredCapability?: string;
  permissions?: string[];
  requiredPermission?: string;
  values?: string[];
  requiredValue?: string | string[];
  organization?: any;
  user?: any;
  admin?: any;
  [key: string]: any;
}

/**
 * Returns true if any of the requiredBusinessLines are available
 *
 * @param {string|array} requiredBusinessLine - The business line required
 * @param {array} businessLines - The businessLines that the requestor has
 */
export const hasBusinessLine = (
  requiredBusinessLine: string | string[] | undefined,
  businessLines?: string[]
): boolean => {
  return hasAnyValues(requiredBusinessLine, businessLines);
};

/**
 * Returns true if the requiredCapability is available
 *
 * @param {string} requiredCapability - The capability required
 * @param {array} capabilities - The capabilities that the requestor has
 */
export const hasCapability = (requiredCapability: string | undefined, capabilities?: string[]): boolean => {
  return hasValue(requiredCapability, capabilities);
};

/**
 * Returns true if the required feature flag is enabled for the user
 *
 * @param {string} flag - The feature flag name
 * @param {string} adminifiUrl - The adminifi URL (needed if features are required from other app)
 * @param {string} authToken - The auth token (needed if features are required from other app)
 */
export const hasFeatureEnabled = async (flag: string, adminifiUrl?: string, authToken?: string): Promise<any> => {
  // Note: you can add feature flags using the Flipper dashboard
  // at '/admin/features/features' in adminifi
  const url = adminifiUrl ? `${adminifiUrl}/api/v1/features.json` : `/api/data/features.json`;

  let features: any;
  if (authToken) {
    features = await loadExternalData(url, authToken);
  } else {
    features = await loadApiData(url);
  }

  const requiredFeature = features && features.find((feature: any) => feature.name === flag);

  return requiredFeature && requiredFeature.enabled;
};

/**
 * Returns true if the required permission exists
 *
 * @param {string} requiredPermission - The permission required
 * @param {array} permissions - The permissions that the requestor has
 */
export const hasPermission = (requiredPermission: string | undefined, permissions?: string[]): boolean => {
  return hasValue(requiredPermission, permissions) || !!(permissions && permissions.includes('all_access'));
};

/**
 * Returns true if the required value exists
 *
 * @param {string} requiredValue - The value required
 * @param {array} values - The values that the requestor has
 */
export const hasValue = (requiredValue: string | undefined, values?: string[]): boolean => {
  if (!requiredValue) {
    return true;
  }
  values = values || [];
  return values.includes(requiredValue);
};

/**
 * Returns true if the any of the required values exists
 *
 * @param {string|array} requiredValues - The values required
 * @param {array} values - The values that the requestor has
 */
export const hasAnyValues = (requiredValues: string | string[] | undefined, values?: string[]): boolean => {
  if (!requiredValues) {
    return true;
  }
  requiredValues = Array.isArray(requiredValues) ? requiredValues : [requiredValues];
  if (requiredValues.length === 0) {
    return true;
  }
  values = values || [];
  return intersection(requiredValues, values).length > 0;
};

/**
 * Returns true if all checks pass
 *
 * @param {object} props - The props that would be passed to AccessControlWrapper
 */
export const hasAccess = (props: AccessControlProps): boolean => {
  return (
    hasRequiredOrganizationType(props) &&
    hasRequiredBusinessLine(props) &&
    hasRequiredCapability(props) &&
    hasRequiredPermission(props) &&
    hasRequiredValue(props)
  );
};

const hasRequiredOrganizationType = (props: AccessControlProps): boolean => {
  const { organizationType, requiredOrganizationType } = props;
  if (!requiredOrganizationType) {
    return true;
  }
  const organizationTypeFromOrg = getProp(props, 'organization.organization_type');
  if (!(organizationType || organizationTypeFromOrg)) {
    return false;
  }
  return requiredOrganizationType === (organizationType || organizationTypeFromOrg);
};

const hasRequiredBusinessLine = (props: AccessControlProps): boolean => {
  const { businessLines, requiredBusinessLine } = props;
  if (!requiredBusinessLine) {
    return true;
  }
  const userBusinessLines = getProp(props, 'user.business_lines');
  if (!(businessLines || userBusinessLines)) {
    return false;
  }
  return hasBusinessLine(requiredBusinessLine, businessLines || userBusinessLines);
};

const hasRequiredCapability = (props: AccessControlProps): boolean => {
  const { capabilities, requiredCapability } = props;
  if (!requiredCapability) {
    return true;
  }
  const capability_list = getProp(props, 'organization.capability_list');
  if (!(capabilities || capability_list)) {
    return false;
  }
  return hasCapability(requiredCapability, capabilities || capability_list);
};

const hasRequiredPermission = (props: AccessControlProps): boolean => {
  const { permissions, requiredPermission } = props;
  if (!requiredPermission) {
    return true;
  }
  const adminPermissions = getProp(props, 'admin.permissions');
  if (!(permissions || adminPermissions)) {
    return false;
  }
  return hasPermission(requiredPermission, permissions || adminPermissions);
};

const hasRequiredValue = (props: AccessControlProps): boolean => {
  const { values, requiredValue } = props;
  if (!requiredValue) {
    return true;
  }
  if (!values) {
    return false;
  }
  return hasAnyValues(requiredValue, values);
};

const getProp = (props: AccessControlProps, propName: string): any => _get(props, propName);
